"use client"
import clsx from "clsx"
import { useLayoutEffect, useRef, useState } from "react"
import { Dialog, DialogContent, DialogPortal } from "./Dialog"
import { DialogFooter, DialogFooterProps } from "./DialogFooter"
import { DialogHeader, DialogHeaderProps } from "./DialogHeader"
import { cn } from "@/utils/classnames"
import { IconButtonVariant } from "@/elements/IconButton/useIconButtonStyles"

export type ModalVariant = "progress" | "warning" | "acknowledgement" | "passive" | "default"

export type ControlledModalHeader = {
  fixed?: boolean
} & DialogHeaderProps

export type ControlledModalFooter = {
  fixed?: boolean
} & DialogFooterProps

interface ControlledModalProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  variant?: ModalVariant
  header?: ControlledModalHeader
  contentClassName?: string
  body?: React.ReactNode
  footer?: { fixed?: boolean } & DialogFooterProps
  preventAutoFocus?: boolean
  zIndex?: number
  onClose?: () => void
  fullScreenOnMobile?: boolean
  closeIconButtonVariant?: IconButtonVariant
}
export const ControlledModal = ({
  open,
  onOpenChange,
  header,
  onClose,
  body,
  footer,
  contentClassName,
  zIndex,
  preventAutoFocus,
  closeIconButtonVariant,
  fullScreenOnMobile = true,
}: ControlledModalProps) => {
  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [footerHeight, setFooterHeight] = useState(0)
  const bodyWithPadding = <div className="grow px-8 pb-8 sm:grow-0">{body}</div>

  useLayoutEffect(() => {
    if (!open) return

    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight

        if (height > 0) {
          setHeaderHeight(height)
        }
      }

      if (footerRef.current) {
        const height = footerRef.current.offsetHeight

        if (height > 0) {
          setFooterHeight(height + 1)
        }
      }
    }

    // Attempt measurement after a micro-task to ensure DOM is ready
    queueMicrotask(updateHeight)

    const resizeObserver = new ResizeObserver(updateHeight)
    headerRef.current && resizeObserver.observe(headerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [open])

  let content: React.ReactNode

  const sharedClasses =
    "flex sm:h-auto h-full max-h-[calc(100vh-var(--custom-height-offset))] flex-col overflow-y-auto sm:max-h-[calc(100vh-40px-var(--custom-height-offset))] md:max-h-[calc(100vh-64px-var(--custom-height-offset))] lg:max-h-[calc(100vh-192px-var(--custom-height-offset))]"

  if (header?.fixed && footer?.fixed) {
    content = (
      <>
        <DialogHeader ref={headerRef} {...header} />
        <div
          style={{
            ["--custom-height-offset" as keyof React.CSSProperties]: `calc(${headerHeight}px + ${footerHeight}px)`,
          }}
          className={sharedClasses}>
          {bodyWithPadding}
        </div>
        <DialogFooter ref={footerRef} {...footer} />
      </>
    )
  } else if (header?.fixed) {
    content = (
      <>
        <DialogHeader ref={headerRef} {...header} />
        {/* max-height is 96px above and below modal - 72px for fixed header (1 line) */}
        <div
          style={{
            ["--custom-height-offset" as keyof React.CSSProperties]: `calc(${headerHeight}px)`,
          }}
          className={sharedClasses}>
          {bodyWithPadding}
          {footer && <DialogFooter ref={footerRef} {...footer} />}
        </div>
      </>
    )
  } else if (footer?.fixed) {
    content = (
      <>
        <div
          style={{
            ["--custom-height-offset" as keyof React.CSSProperties]: `calc(${footerHeight}px)`,
          }}
          className={sharedClasses}>
          <DialogHeader ref={headerRef} {...header} />
          {bodyWithPadding}
        </div>
        {footer && <DialogFooter ref={footerRef} {...footer} />}
      </>
    )
  } else {
    content = (
      <div
        style={{
          ["--custom-height-offset" as keyof React.CSSProperties]: `0px`,
        }}
        className={sharedClasses}>
        <DialogHeader {...header} />
        {bodyWithPadding}
        {footer && <DialogFooter ref={footerRef} {...footer} />}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogContent
          closeIconButtonVariant={closeIconButtonVariant}
          fullScreenOnMobile={fullScreenOnMobile}
          onOpenAutoFocus={e => {
            if (preventAutoFocus) {
              e.preventDefault()
            }
          }}
          onClose={onClose}
          zIndex={zIndex}
          className={cn("min-h-0 md:min-w-[500px]", contentClassName)}
          aria-describedby={header?.subtitle}>
          {content}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
